/**
 * RNR's Text, carrying the design system's type scale instead of shadcn's web one.
 *
 * Six steps and no more — the design file's rule, and the reason the h1…h4/lead/large/muted set
 * that ships with the registry component was replaced rather than added to. Colour is not a variant:
 * it is a class at the call site (`text-muted-foreground`, `text-destructive`), which is how every
 * other RNR component expresses it.
 */
import { familyFor, italicFromClassName, tabularFromClassName, weightFromClassName, type Face } from '@/lib/fonts';
import { cn } from '@/lib/utils';
import { Slot } from '@rn-primitives/slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { Platform, Text as RNText, type Role } from 'react-native';

const textVariants = cva(
  cn(
    // font-sans on the BASE, not only on the variants that override it. Without it the body,
    // small and caption steps carry no family at all and react-native-web falls through to its
    // own system stack -- which is the whole "a named font is a loaded font" failure, and it is
    // invisible on a machine that happens to render Segoe UI at a similar weight.
    'text-foreground text-body font-sans',
    Platform.select({
      web: 'select-text',
    }),
  ),
  {
    variants: {
      variant: {
        // `tabular-nums` on display and nowhere else: the display step is where this app puts a
        // NUMERAL — a score, a verdict, a count — and figures that change width as they change
        // value make a scoreboard jitter every time it refreshes.
        display: 'font-display text-display tabular-nums',
        title: 'font-display text-title',
        heading: 'font-display text-heading',
        body: 'text-body',
        small: 'text-small',
        caption: 'text-caption uppercase',
        /**
         * The boards' tracked uppercase label — a column head, "vs", "PLAYING AS GREG", the theme
         * toggle. It is `caption` plus the one colour it is always drawn in, which is the point:
         * every one of these was `variant="caption" className="text-muted-foreground"` at the call
         * site, and a label that forgot the second half read as body copy in capitals.
         */
        label: 'text-caption uppercase text-muted-foreground',
        /**
         * A SENTENCE at the caption step.
         *
         * `caption` is a LABEL — tracked capitals for a column head, a chip, a "vs" — and it was
         * being handed whole sentences: a screenshot warning, a countdown, every guide's source
         * line. Tracked capitals are how a design shouts, so the quietest thing on the card was
         * arriving as the loudest. Same size, same colour, sentence case, no tracking, and the
         * regular weight a run of prose is set in.
         */
        note: 'text-caption normal-case tracking-normal font-normal leading-[16px] text-muted-foreground',
      },
    },
    defaultVariants: {
      variant: 'body',
    },
  },
);

type TextVariantProps = VariantProps<typeof textVariants>;

type TextVariant = NonNullable<TextVariantProps['variant']>;

const ROLE: Partial<Record<TextVariant, Role>> = {
  display: 'heading',
  title: 'heading',
  heading: 'heading',
};

const ARIA_LEVEL: Partial<Record<TextVariant, string>> = {
  display: '1',
  title: '1',
  heading: '2',
};

/**
 * Which of the two faces a variant is set in. Display type is set in the display face — geometric
 * digits are the point of picking one — and everything else is the body face.
 */
const FACE: Record<TextVariant, Face> = {
  display: 'display',
  title: 'display',
  heading: 'display',
  body: 'sans',
  small: 'sans',
  caption: 'sans',
  label: 'sans',
  note: 'sans',
};

/**
 * The ink a variant carries IN ITS OWN RIGHT, applied over whatever context it is standing in.
 *
 * `label` and `note` are the two variants whose colour is part of what they ARE — both are the
 * muted step by definition — and both were losing it inside a `Card`, whose `TextClassContext`
 * hands every Text below it `text-card-foreground`. The context is merged after the variant, so a
 * note in a card was drawn in the card's near-black body ink: the loudest thing on the card again,
 * by a different route.
 *
 * Ordered between the context and the call site's own className, so a Button's label still takes
 * its context colour (its variant declares none) and `variant="label" className="text-dock-muted"`
 * still ends up dock-muted.
 */
const VARIANT_INK: Partial<Record<TextVariant, string>> = {
  label: 'text-muted-foreground',
  note: 'text-muted-foreground',
};

/** Hoisted, so a `fontVariant` style is the same array object on every render. */
const TABULAR = ['tabular-nums'] as const;

const TextClassContext = React.createContext<string | undefined>(undefined);

function Text({
  className,
  asChild = false,
  variant = 'body',
  ...props
}: React.ComponentProps<typeof RNText> &
  React.RefAttributes<typeof RNText> &
  TextVariantProps & {
    asChild?: boolean;
  }) {
  const textClass = React.useContext(TextClassContext);
  const Component = asChild ? Slot : RNText;

  /**
   * Native only, and deliberately read back out of the class string.
   *
   * Weight and style are expressed at the call site as `font-semibold` and `italic`, which
   * NativeWind turns into a `fontWeight` and a `fontStyle` — and Android ignores both for a loaded
   * font, matching families by exact name instead. So they are read back here and turned into the
   * family that actually carries them: Fraunces' italic is a separate registered face, never a
   * synthesised slant. On the web `familyFor` returns null and the browser does its own matching,
   * as it should.
   */
  const face = FACE[variant ?? 'body'];
  const classes = cn(textClass, className);
  const fontFamily = familyFor(face, weightFromClassName(classes), italicFromClassName(classes));
  /**
   * Tabular figures, the one utility that does not cross on its own.
   *
   * NativeWind parses `font-family`, `font-weight` and `font-style` but not
   * `font-variant-numeric`, so `tabular-nums` is a web-only class unless it is turned into a
   * style here. `fontFamily` being non-null is the native test — the same one, one line up.
   */
  const native = fontFamily !== null;
  const fontVariant = native && tabularFromClassName(classes) ? TABULAR : undefined;

  return (
    <Component
      className={cn(textVariants({ variant }), textClass, VARIANT_INK[variant ?? 'body'], className)}
      role={variant ? ROLE[variant] : undefined}
      aria-level={variant ? ARIA_LEVEL[variant] : undefined}
      {...props}
      style={fontFamily ? [{ fontFamily }, fontVariant && { fontVariant }, props.style] : props.style}
    />
  );
}

export { Text, TextClassContext, textVariants };
export type { TextVariant };
